/* eslint-disable @typescript-eslint/no-explicit-any */
import { Input, InputField, Spinner, Textarea, TextareaInput, View } from '@gluestack-ui/themed'
import React, { PureComponent } from 'react'
import { Platform, StyleSheet, TextInput } from 'react-native'
import Geolocation from 'react-native-geolocation-service'
import { FormAddress } from '../../api/Form/types'
import { AddressResult } from '../../api/Geolocation/types'
import { i18n } from '../../lib/i18n'
import { checkLocation } from '../../services/Permissions/Permissions.service'
import { findAddress } from '../../services/geolocation'
import { theme } from '../../theme'
import { LabelInput } from './components'
import { AddressValue, FormElementProps } from './types'

const initialAddressValue: AddressValue = {
  adresse: '',
  codePostal: '',
  ville: '',
}
interface AddressFieldState {
  loading: boolean
  geolocated: boolean
}

type AddressFieldClassProps = FormElementProps<FormAddress, AddressValue>

class AddressFieldClass extends PureComponent<AddressFieldClassProps, AddressFieldState> {
  private addressInput: TextInput | null = null

  private cityInput: TextInput | null = null

  public state: AddressFieldState = {
    loading: false,
    geolocated: false,
  }

  getAddress = async (lat: number, lng: number) => {
    const adr: AddressResult | null = await findAddress(lat, lng)

    if (adr) {
      const { field } = this.props
      if (field.constraint.detailAddress) {
        if (adr.address) {
          this.setValue('adresse', adr.address)
        }
        if (adr.postalCode) {
          this.setValue('codePostal', adr.postalCode)
        }
        if (adr.city) {
          this.setValue('ville', adr.city)
        }
      } else if (adr.formattedAddress) {
        this.setValue('adresse', adr.formattedAddress)
      }
    }
  }

  async componentDidMount() {
    const allowdLocation = await checkLocation()
    if (allowdLocation) {
      this.setState({
        ...this.state,
        loading: true,
      })

      await this.geolocate()

      if (Platform.OS === 'android') {
        /**
         * If we have twice address on form, the function `Geolocation.getCurrentPosition` work only one time ont Android.
         * With this timeout we force to hide spinner on change state `loading` and try to load again geolocation
         */
        setTimeout(() => {
          if (!this.state.geolocated) {
            this.setState({
              ...this.state,
              loading: false,
            })
            this.geolocate()
          }
        }, 3000)
      }
    }
  }

  geolocate = () => {
    Geolocation.getCurrentPosition(
      position => {
        this.getAddress(position.coords.latitude, position.coords.longitude)
        this.setState({
          ...this.state,
          loading: false,
          geolocated: true,
        })
      },
      () => {
        this.setState({
          ...this.state,
          loading: false,
        })
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 },
    )
  }

  public focus = () => {
    this.addressInput?.focus()
  }

  private focusCity = () => {
    this.cityInput?.focus()
  }

  private renderAddressField() {
    const { field, handleBlur } = this.props
    const height = field.constraint.height || theme.textSize.x2
    const numberOfLines = height / theme.textSize.x2
    const address = this.getAddressFieldValue('adresse')

    return (
      <View>
        <LabelInput text={field.libelle} required={field.constraint.mandatory} />
        {this.state.loading && <Spinner />}
        {!this.state.loading && (
          <Textarea style={{ minHeight: height }}>
            <TextareaInput
              ref={(input: any) => (this.addressInput = input)}
              numberOfLines={numberOfLines}
              scrollEnabled={false}
              onChangeText={(text: string) => this.setValue('adresse', text)}
              onBlur={() => handleBlur(this.elementId)}
              returnKeyType="default"
              value={address}
            />
          </Textarea>
        )}
      </View>
    )
  }

  private renderZipAndCityFields() {
    const { field, returnKeyType, handleBlur } = this.props
    if (!field.constraint.detailAddress) {
      return null
    }
    const zipCode = this.getAddressFieldValue('codePostal')
    const city = this.getAddressFieldValue('ville')

    return (
      <>
        <View style={styles.input}>
          <LabelInput text={i18n.t('form.zipCode')} required={field.constraint.mandatory} />
          <Input variant="outline">
            <InputField returnKeyType="next" onChangeText={text => this.setValue('codePostal', text)} onSubmitEditing={this.focusCity} value={zipCode} />
          </Input>
        </View>
        <View style={styles.input}>
          <LabelInput text={i18n.t('form.city')} required={field.constraint.mandatory} />
          <Input variant="outline">
            <InputField
              ref={(input: any) => (this.cityInput = input)}
              returnKeyType={returnKeyType}
              onChangeText={text => this.setValue('ville', text)}
              onBlur={() => handleBlur(this.elementId)}
              value={city}
            />
          </Input>
        </View>
      </>
    )
  }

  private get elementId() {
    return `${this.props.field.id}`
  }

  private setValue(key: keyof AddressValue, newAddress: string): void {
    const { value, setFieldValue } = this.props
    setFieldValue(this.elementId, {
      ...(value || initialAddressValue),
      [key]: newAddress,
    })
  }

  private getAddressFieldValue(key: keyof AddressValue): string {
    const { value, setFieldValue } = this.props
    if (!value) {
      setFieldValue(this.elementId, initialAddressValue)
      return initialAddressValue[key] || ''
    }
    return value[key] || ''
  }

  public render() {
    return (
      <>
        {this.renderAddressField()}
        {this.renderZipAndCityFields()}
      </>
    )
  }
}

const styles = StyleSheet.create({
  input: {
    marginVertical: theme.padding.x1,
  },
  inputField: {
    borderWidth: 1,
    borderColor: theme.colors.silver,
    borderRadius: 4,
    paddingHorizontal: theme.padding.x2,
    paddingVertical: theme.padding.x1,
    fontSize: theme.textSize.x1_75,
  },
  textArea: {
    borderWidth: 1,
    borderColor: theme.colors.silver,
    borderRadius: 4,
    paddingHorizontal: theme.padding.x2,
    paddingVertical: theme.padding.x1,
    fontSize: theme.textSize.x1_75,
    textAlignVertical: 'top',
  },
})

export const AddressField = AddressFieldClass
